import {
  ButtonLight,
  Box,
  Modal,
  Link,
  Switch,
  Flex,
  FormControl,
  ButtonDanger,
  FullBlockSpinner
} from '../../components'
import { t, Trans } from '@lingui/macro'
import IconBadge from '../../components/common/icon/IconBadge'
import { linkBuilder } from 'shared/linkBuilder'
import React, { useState } from 'react'
import { useEventRelatedEntitiesQuery } from './event-edit/EventEdit.codegen'
import { useManageEventContext } from './ManageEventContext'
import { useRecurringEventRelatedEntitiesQuery } from './event-edit/RecurringEventEdit.codegen'
import { SenderSelect } from 'front-end/src/pages/manage-campaign-page/campaign-new-action/SenderSelect'
import { EventQueryAccessor } from 'front-end/src/pages/manage-events/event-edit/helpers'
import { EventUsedInCampaignsWarning } from 'front-end/src/pages/manage-events/EventUsedInCampaignsWarning'

export const EventDeleteModal = ({ resolve, reject, disableActions }) => {
  const [emailIdentityId, setEmailIdentityId] = useState<number | null>(null)
  const [notifyInvitees, setNotifyInvitees] = useState(false)
  const { event, isRecurringSeries } = useManageEventContext()

  const query = isRecurringSeries
    ? useRecurringEventRelatedEntitiesQuery
    : useEventRelatedEntitiesQuery

  const key = isRecurringSeries
    ? EventQueryAccessor.recurringEvent
    : EventQueryAccessor.event

  const { data, loading } = query({
    variables: {
      eventId: event.id
    }
  })

  if (loading) {
    return <FullBlockSpinner />
  }

  const relatedCampaigns = data.admin[key].relatedCampaigns

  if (!event) {
    return null
  }

  if (event.relatedCertifications && event.relatedCertifications?.length > 0) {
    return (
      <Modal
        newDesign
        header={t`Unable to cancel event ${event.title}`}
        controls={[
          <ButtonLight onClick={reject} key={1}>
            <Trans>Cancel</Trans>
          </ButtonLight>
        ]}
      >
        <p className={`Modal__relatedItems__importantInfo`}>
          <Trans>
            This event cannot be archived as it is part of a certificate.
          </Trans>
        </p>
        <p className={`Modal__relatedItems__importantInfo`}>
          <Trans>
            Should you still wish to archive this event please contact the below
            owner as they may want to make the event private to maintain it in
            the certificate.
          </Trans>
        </p>

        <div className={'Modal__relatedItems'}>
          <div className={'Modal__relatedItems__column'}>
            <IconBadge size={'large'} />
          </div>
          <div className={'Modal__relatedItems__column'}>
            <p>
              <Trans>This event appears in:</Trans>
            </p>
            <ul>
              {event.relatedCertifications?.map((certification) => {
                return (
                  <li key={certification.id}>
                    <Link
                      href={`${linkBuilder.certificationsDetail(
                        certification.id
                      )}`}
                    >
                      <strong>{certification.name}</strong>{' '}
                      {certification.author && (
                        <small>
                          (<Trans>by</Trans> {certification.author.userIdentity}
                          )
                        </small>
                      )}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      cannotBeHidden={disableActions}
      controls={[
        <ButtonLight onClick={reject} key={1} disabled={disableActions}>
          <Trans>Cancel</Trans>
        </ButtonLight>,
        <ButtonDanger
          onClick={async () => {
            await resolve({ notifyInvitees, emailIdentityId })
          }}
          key={2}
          isLoading={disableActions}
        >
          <Trans>Archive</Trans>
        </ButtonDanger>
      ]}
    >
      <Box pt="48px" textAlign="left">
        <Box fontSize="18px" mb="24px">
          {isRecurringSeries ? (
            <Trans>
              Are you sure you want to{' '}
              <strong>archive this event series?</strong>
            </Trans>
          ) : (
            <Trans>
              Are you sure you want to <strong>archive this event?</strong>
            </Trans>
          )}
        </Box>

        <EventUsedInCampaignsWarning relatedCampaigns={relatedCampaigns} />

        {event.inviteesCount > 0 && (
          <>
            <FormControl
              as={Flex}
              alignItems="center"
              mb="24px"
              maxW="60%"
              justifyContent="space-between"
            >
              <Trans>Notify invitees via email</Trans>

              <Switch
                isChecked={notifyInvitees}
                onChange={(e) => setNotifyInvitees(e.target.checked)}
                dataTest="EmailInviteesSwitch"
              />
            </FormControl>

            {notifyInvitees && (
              <SenderSelect
                value={emailIdentityId}
                onChange={(id) => setEmailIdentityId(id)}
              />
            )}
          </>
        )}
      </Box>
    </Modal>
  )
}
